import { saveAs } from 'file-saver';
import { pdf } from '@react-pdf/renderer';

import { printPdfBlob } from './printPdfBlob';
import { ReportPDF } from '../components';
import ReportPDFHolidays from '../components/reports/shared/ReportPDFHolidays';
import ReportPDFAttendance from '../components/reports/shared/ReportPDFAttendance';
import HashResultTemplatePDF from '../components/reports/hash/HashResultTemplatePDF';
import { KIND_OF_REPORTS } from '../../constants';
import IncidentsReportTemplatePDF from '../components/reports/shared/IncidentsReportTemplatePDF';

/**
 * Factory that determines which PDF component to use based on report type
 * @param {string} reportType - Type of report (holidays, attendance, etc.)
 * @param {Object} props - Props to pass to the component
 * @returns {React.Component} - Appropriate PDF component
 */
const getPDFComponent = (reportType, props) => {
  switch (reportType) {
    case KIND_OF_REPORTS.HOLIDAYS:
      return <ReportPDFHolidays {...props} />;
    case KIND_OF_REPORTS.ATTENDANCE:
      return <ReportPDFAttendance {...props} />;
    case KIND_OF_REPORTS.HASH: {
      const hashData = props?.data || {};

      return (
        <HashResultTemplatePDF
          {...hashData}
          {...props}
          dailyReport={props?.dailyReport || hashData?.dailyReport}
        />
      );
    }
    case KIND_OF_REPORTS.ISSUES:
      return <IncidentsReportTemplatePDF {...props} />;
    default:
      return <ReportPDF {...props} />;
  }
};

/**
 * Generates a PDF blob for a report type.
 * @param {Object} params - Report parameters
 * @returns {Promise<Blob>}
 */
export const generateReportPdfBlob = async ({
  data,
  title,
  reportType,
  orientation = 'portrait',
  includeGlosary = true,
  ...additionalParams
}) => {
  const pdfComponent = getPDFComponent(reportType, {
    data,
    title,
    orientation,
    includeGlosary,
    ...additionalParams,
  });

  return pdf(pdfComponent).toBlob();
};

/**
 * Enhanced PDF download function that works with different report types
 * @param {Object} params - Download parameters
 * @param {Object} params.data - Report data
 * @param {string} params.title - Report title
 * @param {string} params.fileName - File name
 * @param {string} params.reportType - Type of report (holidays, attendance, etc.)
 * @param {string} params.orientation - PDF orientation
 * @param {boolean} params.includeGlosary - Whether to include glossary
 * @param {...Object} additionalParams - Additional parameters for specific report types
 */
export const downloadReportAsPdf = async ({
  fileName,
  reportType,
  ...pdfParams
}) => {
  try {
    const blob = await generateReportPdfBlob({ ...pdfParams, reportType });

    const currentDate = new Date().toLocaleDateString('es-ES').replace(/\//g, '/');
    saveAs(blob, `${fileName}_${currentDate}.pdf`);
  } catch (error) {
    console.error('Error downloading report as PDF:', error);
  }
};

/**
 * Opens the browser print dialog for a report PDF.
 * @param {Object} params - Same parameters as downloadReportAsPdf (fileName is ignored)
 */
export const printReportAsPdf = async ({ reportType, ...pdfParams }) => {
  try {
    const blob = await generateReportPdfBlob({ ...pdfParams, reportType });
    await printPdfBlob(blob);
  } catch (error) {
    console.error('Error printing report as PDF:', error);
  }
};

/**
 * Factory for Excel download based on report type
 * @param {Object} params - Download parameters
 */
export const downloadReportAsExcel = async ({
  data,
  fileName,
  reportTitle,
  reportType,
  includeGlosary = true,
  emptyUserDataLines = [],
}) => {
  try {
    switch (reportType) {
      case KIND_OF_REPORTS.ATTENDANCE:
        const { downloadAttendanceReportAsExcel } = await import(
          './downloadAttendanceReportAsExcel'
        );
        return downloadAttendanceReportAsExcel(
          data,
          fileName,
          reportTitle,
          includeGlosary,
          emptyUserDataLines,
        );
      case KIND_OF_REPORTS.HOLIDAYS:
        const { downloadHolidaysReportAsExcel } = await import('./downloadHolidaysReportAsExcel');
        return downloadHolidaysReportAsExcel(
          data,
          fileName,
          reportTitle,
          includeGlosary,
          emptyUserDataLines,
        );
      case KIND_OF_REPORTS.ISSUES:
        const { downloadIncidentsReportAsExcel } = await import('./downloadIncidentsReportAsExcel');
        return downloadIncidentsReportAsExcel(data, fileName, reportTitle, includeGlosary);
      default:
        const { downloadReportAsExcel: defaultExcel } = await import('./downloadReportAsExcel');
        return defaultExcel(
          data,
          fileName,
          reportTitle,
          includeGlosary,
          emptyUserDataLines,
        );
    }
  } catch (error) {
    console.error('Error downloading report as Excel:', error);
  }
};

/**
 * Factory for DOCX download based on report type
 * @param {Object} params - Download parameters
 */
export const downloadReportAsDocx = async ({
  data,
  fileName,
  reportTitle,
  reportType,
  includeGlosary = true,
  emptyUserDataLines = [],
}) => {
  try {
    // Import the specific DOCX function based on report type
    switch (reportType) {
      case KIND_OF_REPORTS.ATTENDANCE:
        const { downloadAttendanceReportAsDocx } = await import('./downloadAttendanceReportAsDocx');
        return downloadAttendanceReportAsDocx(
          data,
          fileName,
          reportTitle,
          includeGlosary,
          emptyUserDataLines,
        );
      case KIND_OF_REPORTS.HOLIDAYS:
        const { downloadHolidaysReportAsDocx } = await import('./downloadHolidaysReportAsDocx');
        return downloadHolidaysReportAsDocx(
          data,
          fileName,
          reportTitle,
          includeGlosary,
          emptyUserDataLines,
        );
      case KIND_OF_REPORTS.ISSUES:
        const { downloadIncidentsReportAsDocx } = await import('./downloadIncidentsReportAsDocx');
        return downloadIncidentsReportAsDocx(data, fileName, reportTitle, includeGlosary);
      default:
        const { downloadReportAsDocx: defaultDocx } = await import('./downloadReportAsDocx');
        return defaultDocx(
          data,
          fileName,
          reportTitle,
          includeGlosary,
          emptyUserDataLines,
        );
    }
  } catch (error) {
    console.error('Error downloading report as DOCX:', error);
  }
};

/**
 * Main factory function that provides all download methods for a specific report type
 * @param {string} reportType - Type of report
 * @returns {Object} - Object with download methods for the report type
 */
export const getReportDownloadMethods = reportType => {
  return {
    downloadPdf: params => downloadReportAsPdf({ ...params, reportType }),
    printPdf: params => printReportAsPdf({ ...params, reportType }),
    downloadExcel: params => downloadReportAsExcel({ ...params, reportType }),
    downloadDocx: params => downloadReportAsDocx({ ...params, reportType }),
  };
};
